import {
  CopyYoutubeLinkRequest,
  CopyYoutubeLinkResponse,
  CreateDemoRequest,
  CreateDemoResponse,
  CreateVariantRequest,
  CreateVariantResponse,
  CreateVariantVersionRequest,
  CreateVariantVersionResponse,
  DeleteDemoRequest,
  DeleteDemoResponse,
  DeleteMediaRequest,
  DeleteMediaResponse,
  DeleteSupportingMediaRequest,
  DeleteSupportingMediaResponse,
  DeleteVariantRequest,
  DeleteVariantResponse,
  Demo,
  DemoRender,
  DemoVariant,
  DemoVariantVersion,
  ExportDemoVariantRequest,
  ExportDemoVariantResponse,
  GetDemoRequest,
  GetDemoResponse,
  GetMediaUploadRequest,
  GetMediaUploadResponse,
  GetSupportingMediaUploadUrlRequest,
  GetSupportingMediaUploadUrlResponse,
  GetSupportingMediaDownloadUrlRequest,
  GetSupportingMediaDownloadUrlResponse,
  ListDemosRequest,
  ListDemosResponse,
  ListVariantVersionsRequest,
  ListVariantVersionsResponse,
  MarkVariantAsDefaultRequest,
  MarkVariantAsDefaultResponse,
  MediaUploadedRequest,
  MediaUploadedResponse,
  PublishVariantVersionRequest,
  PublishVariantVersionResponse,
  RenameMediaRequest,
  RenameMediaResponse,
  SaveDemoRequest,
  SaveDemoResponse,
  SaveVariantRequest,
  SaveVariantResponse,
  SaveVariantVersionPagesRequest,
  SaveVariantVersionPagesResponse,
  SupportingMediaUploadedRequest,
  SupportingMediaUploadedResponse,
} from "../types/demo";
import { ACTION_TYPES, postTypedData, putData } from "./modelUtils";
import { demoTypeExternalUrl } from "../../config/CommonLists";
import { logger } from "../../utils/LogUtils";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);

/**
 * Fetch a variant for this language and vertical
 * note this did take select items previously now takes the .value as a string
 */
export function getVariant(demo: Demo, language: string, vertical: string) {
  const variant = demo.variants.find((variant) => {
    return variant.language === language && variant.vertical === vertical;
  });

  return variant;
}

/**
 * Checks to see if this is an external url demo
 */
export function isExternalDemo(
  demoVariantVersion: DemoVariantVersion
): boolean {
  return (
    demoVariantVersion?.type === demoTypeExternalUrl &&
    demoVariantVersion?.url !== undefined &&
    demoVariantVersion?.url !== "NONE"
  );
}

/**
 * Create a demo
 */
export async function createDemo(
  request: CreateDemoRequest
): Promise<CreateDemoResponse> {
  return postTypedData(ACTION_TYPES.CREATE_DEMO, request, "create demo");
}

/**
 * Deletes demos
 */
export async function deleteDemos(
  requests: DeleteDemoRequest[]
): Promise<DeleteDemoResponse[]> {
  const responses: DeleteDemoResponse[] = [];

  for (var i = 0; i < requests.length; i++) {
    responses.push(await deleteDemo(requests[i]));
  }

  return responses;
}

/**
 * Deletes a demo
 */
export async function deleteDemo(
  request: DeleteDemoRequest
): Promise<DeleteDemoResponse> {
  return postTypedData(ACTION_TYPES.DELETE_DEMO, request, "delete demo");
}

/**
 * Exports a demo variant
 */
export async function exportDemoVariant(
  request: ExportDemoVariantRequest
): Promise<ExportDemoVariantResponse> {
  return postTypedData(
    ACTION_TYPES.EXPORT_DEMO_VARIANT,
    request,
    "export demo variant"
  );
}

/**
 * Deletes a variant
 */
export async function deleteVariant(
  request: DeleteVariantRequest
): Promise<DeleteVariantResponse> {
  return postTypedData(ACTION_TYPES.DELETE_VARIANT, request, "delete variant");
}

/**
 * Fetches the upload url for a supporting media item
 */
export async function getSupportingMediaUploadUrl(
  request: GetSupportingMediaUploadUrlRequest
): Promise<GetSupportingMediaUploadUrlResponse> {
  return postTypedData(
    ACTION_TYPES.GET_SUPPORTING_MEDIA_UPLOAD_URL,
    request,
    "get supporting media upload"
  );
}

/**
 * Tell the server that the supporting media has been uploaded
 */
export async function supportingMediaUploaded(
  request: SupportingMediaUploadedRequest
): Promise<SupportingMediaUploadedResponse> {
  return postTypedData(
    ACTION_TYPES.SUPPORTING_MEDIA_UPLOADED,
    request,
    "supporting media uploaded"
  );
}

/**
 * Deletes supporting media from the draft version
 * of this variant
 */
export async function deleteSupportingMedia(
  request: DeleteSupportingMediaRequest
): Promise<DeleteSupportingMediaResponse> {
  return postTypedData(
    ACTION_TYPES.DELETE_SUPPORTING_MEDIA,
    request,
    "delete supporting media"
  );
}

/**
 * Deletes media from the draft version of this variant
 */
export async function deleteMedia(
  request: DeleteMediaRequest
): Promise<DeleteMediaResponse> {
  return postTypedData(ACTION_TYPES.DELETE_MEDIA, request, "delete media");
}

/**
 * Renames media from the draft version of this variant
 */
export async function renameMedia(
  request: RenameMediaRequest
): Promise<RenameMediaResponse> {
  return postTypedData(ACTION_TYPES.RENAME_MEDIA, request, "rename media");
}

/**
 * Renames media from the draft version of this variant
 */
export async function mediaUploaded(
  request: MediaUploadedRequest
): Promise<MediaUploadedResponse> {
  return postTypedData(ACTION_TYPES.MEDIA_UPLOADED, request, "media uploaded");
}

/**
 * Creates a new variant
 */
export async function createVariant(
  request: CreateVariantRequest
): Promise<CreateVariantResponse> {
  return postTypedData(ACTION_TYPES.CREATE_VARIANT, request, "create variant");
}

/**
 * Marks a variant as the default variant
 */
export async function markVariantAsDefault(
  request: MarkVariantAsDefaultRequest
): Promise<MarkVariantAsDefaultResponse> {
  return postTypedData(
    ACTION_TYPES.MARK_VARIANT_AS_DEFAULT,
    request,
    "mark variant as default"
  );
}

/**
 * Fetches an upload url fo a media item
 */
export async function getMediaUploadUrl(
  request: GetMediaUploadRequest
): Promise<GetMediaUploadResponse> {
  return postTypedData(
    ACTION_TYPES.GET_MEDIA_UPLOAD_URL,
    request,
    "get media upload url"
  );
}

/**
 * Saves a variant
 */
export async function saveVariant(
  request: SaveVariantRequest
): Promise<SaveVariantResponse> {
  return postTypedData(ACTION_TYPES.SAVE_VARIANT, request, "save variant");
}

/**
 * Saves the pages on a draft variant version
 */
export async function saveVariantVersionPages(
  request: SaveVariantVersionPagesRequest
): Promise<SaveVariantVersionPagesResponse> {
  return postTypedData(
    ACTION_TYPES.SAVE_VARIANT_VERSION_PAGES,
    request,
    "save variant version pages"
  );
}

/**
 * Saves a demo
 */
export async function saveDemo(
  request: SaveDemoRequest
): Promise<SaveDemoResponse> {
  return postTypedData(ACTION_TYPES.SAVE_DEMO, request, "save demo");
}

/**
 * Lists demos using a filter
 */
export async function listDemos(
  request: ListDemosRequest
): Promise<ListDemosResponse> {
  return postTypedData(ACTION_TYPES.LIST_DEMOS, request, "list demos");
}

/**
 * Lists variant versions
 */
export async function listVariantVersions(
  request: ListVariantVersionsRequest
): Promise<ListVariantVersionsResponse> {
  return postTypedData(
    ACTION_TYPES.LIST_VARIANT_VERSIONS,
    request,
    "list variant versions"
  );
}

/**
 * Creates a variant version
 */
export async function createVariantVersion(
  request: CreateVariantVersionRequest
): Promise<CreateVariantVersionResponse> {
  return postTypedData(
    ACTION_TYPES.CREATE_VARIANT_VERSION,
    request,
    "create variant version"
  );
}

/**
 * Publishes a variant version
 */
export async function publishVariantVersion(
  request: PublishVariantVersionRequest
): Promise<PublishVariantVersionResponse> {
  return postTypedData(
    ACTION_TYPES.PUBLISH_VARIANT_VERSION,
    request,
    "publish variant version"
  );
}

/**
 * Fetches a demo
 */
export async function getDemo(
  request: GetDemoRequest
): Promise<GetDemoResponse> {
  return postTypedData(ACTION_TYPES.GET_DEMO, request, "get demo");
}

/**
 * Prepares a demo for rendering
 */
export function prepareDemo(demo: Demo): DemoRender {
  const languages = new Set<string>();
  const verticals = new Set<string>();
  const types = new Set<string>();
  const languageVerticals: Record<string, Set<string>> = {};
  let availableWhen = "Now";

  demo.variants.forEach((v) => {
    languages.add(v.language);
    verticals.add(v.vertical);

    if (languageVerticals[v.language] === undefined) {
      languageVerticals[v.language] = new Set<string>();
    }

    languageVerticals[v.language].add(v.vertical);

    if (v.tags === undefined) {
      v.tags = [];
    }
  });

  const now = dayjs();

  if (dayjs(demo.available).isBefore(now)) {
    availableWhen = "Now";
  } else {
    const seconds = dayjs(demo.available).diff(now, "seconds");

    if (seconds < 60) {
      availableWhen = "< 1 minute";
    } else if (seconds < 3600) {
      availableWhen = `${Math.floor(seconds / 60)} minutes`;
    } else if (seconds < 86400) {
      availableWhen = `${Math.floor(seconds / 3600)} hours`;
    } else {
      availableWhen = `${Math.floor(seconds / 86400)} days`;
    }
  }

  /**
   * Compute a mapping between languages and supported verticals
   */
  const languageVerticalKeys = Object.keys(languageVerticals);
  const languageVerticalsMap = new Map<string, string[]>();

  languageVerticalKeys.forEach((key) => {
    languageVerticalsMap.set(
      key,
      Array.from(languageVerticals[key]).sort() as string[]
    );
  });

  const demoRender: DemoRender = {
    ...demo,
    languageVerticals: languageVerticalsMap,
    languages: Array.from(languages).sort() as string[],
    verticals: Array.from(verticals).sort() as string[],
    types: Array.from(types).sort() as string[],
    availableWhen: availableWhen,
    availableString: dayjs(demo.available).utc().format("DD/MM/YYYY hh:mm"),
    createdString: dayjs(demo.created).utc().format("DD/MM/YYYY hh:mm"),
    updatedString: dayjs(demo.updated).utc().format("DD/MM/YYYY hh:mm"),
  };

  logger.info("prepareDemo() Made demo render version");
  logger.dir(demoRender);
  return demoRender;
}

/**
 * Is this the default variant?
 */
export function isDefaultVariant(
  demo: Demo,
  language: string,
  vertical: string
) {
  const variant = demo.variants.find(
    (v) => v.language === language && v.vertical === vertical
  );

  if (variant !== undefined && variant.defaultVariant === true) {
    return true;
  }

  return false;
}

/**
 * Is this the selected variant?
 */
export function isSelectedVariant(
  variant: DemoVariant,
  language: string,
  vertical: string
) {
  return language === variant.language && vertical === variant.vertical;
}

/**
 * Fetches the default variant or undefined
 */
export function getDefaultVariant(demo: Demo) {
  return demo.variants.find((v) => v.defaultVariant === true);
}

/**
 * Uploads a file to a presigned url in S3 with retries
 */
export async function uploadFile(
  url: string,
  file: File,
  progressFunction: (percentCompleted: number) => void = undefined
) {
  var retry = 0;
  var maxRetries = 10;

  while (retry < maxRetries) {
    try {
      var options = {
        headers: {
          "Content-Type": file.type,
          "Cache-Control": `public, max-age=31536000, immutable`,
        },
        onUploadProgress: function (progressEvent) {
          var percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          if (progressFunction) {
            progressFunction(percentCompleted);
          }
        },
      };

      await putData(url, file, options);

      logger.info("Upload completed successfully");

      return;
    } catch (error: any) {
      logger.error(
        `Failed to upload media in retry: ${retry} of ${maxRetries}`,
        error
      );
    }

    retry++;
  }

  if (retry === maxRetries) {
    throw new Error(`Failed to load media, max retries reached`);
  }
}

/**
 * Uploads a buffer to a presigned url in S3 with retries
 */
export async function uploadBuffer(
  url: string,
  contentType: string,
  buffer: ArrayBufferLike,
  progressFunction: (percentCompleted: number) => void = undefined
) {
  var retry = 0;
  var maxRetries = 10;

  while (retry < maxRetries) {
    try {
      var options = {
        headers: {
          "Content-Type": contentType,
          "Cache-Control": `public, max-age=31536000, immutable`,
        },
        onUploadProgress: function (progressEvent) {
          var percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          if (progressFunction) {
            progressFunction(percentCompleted);
          }
        },
      };

      await putData(url, buffer, options);

      logger.info("Upload from buffer completed successfully");

      return;
    } catch (error: any) {
      logger.error(
        `Failed to upload media from buffer in retry: ${retry} of ${maxRetries}`,
        error
      );
    }

    retry++;
  }

  throw new Error(
    `Failed to upload media after ${maxRetries} attempts. Please try again.`
  );
}

export const isVariantAvailable = (variant: DemoVariant) => {
  if (variant.available === undefined) {
    logger.error(
      "isVariantAvailable() found undefined variant.available, making unavailable"
    );
    return false;
  }
  return variant.enabled === true && dayjs(variant.available) < dayjs().utc();
};

export const isDemoAvailable = (demo: Demo) => {
  if (demo.available === undefined) {
    logger.error(
      "isDemoAvailable() found undefined variant.available, making unavailable"
    );
    return false;
  }
  return demo.enabled === true && dayjs(demo.available) < dayjs().utc();
};

/**
 * Gets a supporting media download URL
 */
export async function getSupportingMediaDownloadUrl(
  request: GetSupportingMediaDownloadUrlRequest
): Promise<GetSupportingMediaDownloadUrlResponse> {
  return postTypedData(
    ACTION_TYPES.GET_SUPPORTING_MEDIA_DOWNLOAD_URL,
    request,
    "get supporting media download url"
  );
}

/**
 * Copies YouTube link and creates demo play history entry
 */
export async function copyYoutubeLink(
  request: CopyYoutubeLinkRequest
): Promise<CopyYoutubeLinkResponse> {
  return postTypedData(
    ACTION_TYPES.COPY_YOUTUBE_LINK,
    request,
    "copy youtube link"
  );
}
